import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, PlayCircle, UploadCloud, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminFetch, type MlCurationJson, type MlMediaItem } from '@/lib/api';

type ReviewFilter = 'pending' | 'approved' | 'rejected' | 'all';

export function MediaPage() {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const reviewFromUrl = searchParams.get('review_status');
  const initialReview: ReviewFilter =
    reviewFromUrl === 'approved' ||
    reviewFromUrl === 'rejected' ||
    reviewFromUrl === 'all' ||
    reviewFromUrl === 'pending'
      ? reviewFromUrl
      : 'pending';
  const [mediaKind, setMediaKind] = useState<'all' | 'photo' | 'video'>('all');
  const [reviewStatus, setReviewStatus] = useState<ReviewFilter>(initialReview);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [activeItem, setActiveItem] = useState<MlMediaItem | null>(null);
  const [exportFeedback, setExportFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const qc = useQueryClient();

  const queryKey = useMemo(
    () => ['admin-media', clientId, mediaKind, reviewStatus],
    [clientId, mediaKind, reviewStatus],
  );

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      if (mediaKind !== 'all') params.set('media_kind', mediaKind);
      if (reviewStatus !== 'all') params.set('review_status', reviewStatus);
      params.set('per_page', '60');
      return adminFetch<{ media: MlMediaItem[]; meta: { total: number } }>(`/ml/media?${params}`);
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-media'] });

  const bulkReview = useMutation({
    mutationFn: (status: ReviewFilter) =>
      adminFetch('/ml/media/bulk-review', {
        method: 'POST',
        body: JSON.stringify({ asset_ids: [...selected], review_status: status }),
      }),
    onSuccess: () => {
      setSelected(new Set());
      invalidate();
    },
  });

  const exportApproved = useMutation({
    mutationFn: (format: 'roboflow_manifest' | 'zip_bundle' | 'roboflow_upload') => {
      setExportFeedback(null);
      const body: Record<string, unknown> = { format, only_approved: true };
      if (clientId) body.client_id = Number(clientId);
      if (mediaKind !== 'all') body.media_kind = mediaKind;
      return adminFetch<{
        export: { id: number; download_url: string | null; asset_count: number | null; error_message: string | null };
      }>('/ml/exports', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: (result, format) => {
      if (format === 'roboflow_upload') {
        const count = result.export.asset_count ?? 0;
        setExportFeedback({
          kind: result.export.error_message ? 'error' : 'success',
          message:
            `${count} imagem(ns) enviada(s) ao Roboflow.` +
            (result.export.error_message ? ` ${result.export.error_message}` : ''),
        });
        invalidate();
        return;
      }
      if (result.export.download_url) window.open(result.export.download_url, '_blank');
    },
    onError: (err) => {
      setExportFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Erro ao exportar.',
      });
    },
  });

  const items = data?.media ?? [];

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Curadoria ML</h1>
          <p className="text-sm text-neutral-400">
            {data?.meta.total ?? 0} itens · só <strong className="text-white">aprovados</strong> vão para export/Roboflow
            {clientId ? ` · cliente #${clientId}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={reviewStatus}
            onChange={(e) => setReviewStatus(e.target.value as ReviewFilter)}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm"
          >
            <option value="pending">Pendentes</option>
            <option value="approved">Aprovados</option>
            <option value="rejected">Rejeitados</option>
            <option value="all">Todos</option>
          </select>
          <select
            value={mediaKind}
            onChange={(e) => setMediaKind(e.target.value as 'all' | 'photo' | 'video')}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm"
          >
            <option value="all">Todos</option>
            <option value="photo">Fotos</option>
            <option value="video">Vídeos</option>
          </select>
          {selected.size > 0 && (
            <>
              <button
                type="button"
                onClick={() => bulkReview.mutate('approved')}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white"
              >
                <Check className="h-4 w-4" /> Aprovar ({selected.size})
              </button>
              <button
                type="button"
                onClick={() => bulkReview.mutate('rejected')}
                className="inline-flex items-center gap-1 rounded-lg bg-red-900 px-3 py-2 text-sm"
              >
                <X className="h-4 w-4" /> Rejeitar
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => exportApproved.mutate('zip_bundle')}
            disabled={exportApproved.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-black disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            ZIP aprovados
          </button>
          <button
            type="button"
            onClick={() => exportApproved.mutate('roboflow_upload')}
            disabled={exportApproved.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            <UploadCloud className="h-4 w-4" />
            {exportApproved.isPending && exportApproved.variables === 'roboflow_upload'
              ? 'Enviando…'
              : 'Enviar ao Roboflow'}
          </button>
        </div>
      </div>

      {exportFeedback && (
        <p className={exportFeedback.kind === 'error' ? 'text-red-400' : 'text-emerald-400'}>
          {exportFeedback.message}
        </p>
      )}

      {isLoading && <p className="text-neutral-400">Carregando mídia…</p>}
      {error && <p className="text-red-400">{error instanceof Error ? error.message : 'Erro'}</p>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {items.map((item) => (
          <MediaCard
            key={item.id}
            item={item}
            selected={selected.has(item.id)}
            onToggleSelect={() => toggleSelect(item.id)}
            onOpen={() => setActiveItem(item)}
          />
        ))}
      </div>

      {activeItem && (
        <CurationPanel
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onSaved={() => {
            invalidate();
            setActiveItem(null);
          }}
        />
      )}
    </div>
  );
}

function MediaCard({
  item,
  selected,
  onToggleSelect,
  onOpen,
}: {
  item: MlMediaItem;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}) {
  const preview = item.media_kind === 'video' ? item.thumbnail_url ?? item.url : item.url;
  const animalNumber =
    (item.export_labels.internal_number as string | undefined) ??
    (item.animal_snapshot?.internal_number as string | undefined);

  const statusColor =
    item.review_status === 'approved'
      ? 'border-emerald-600'
      : item.review_status === 'rejected'
        ? 'border-red-800 opacity-60'
        : 'border-amber-700';

  return (
    <div className={`relative aspect-square overflow-hidden rounded-lg border-2 bg-neutral-900 ${statusColor}`}>
      <button type="button" onClick={onOpen} className="absolute inset-0 z-0">
        {preview ? (
          <img src={preview} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-neutral-600">sem preview</div>
        )}
        {item.media_kind === 'video' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
            <PlayCircle className="h-8 w-8 text-white drop-shadow" />
          </div>
        )}
      </button>
      <label className="absolute left-2 top-2 z-10">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="h-4 w-4" />
      </label>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 text-[10px]">
        <div>{animalNumber ?? '—'}</div>
        <div className="text-neutral-400 capitalize">{item.review_status}</div>
      </div>
    </div>
  );
}

function CurationPanel({
  item,
  onClose,
  onSaved,
}: {
  item: MlMediaItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const curation = item.curation_json ?? {};
  const [internalNumber, setInternalNumber] = useState(
    String(curation.internal_number ?? item.export_labels.internal_number ?? ''),
  );
  const [animalName, setAnimalName] = useState(String(curation.animal_name ?? item.export_labels.name ?? ''));
  const [roboflowSplit, setRoboflowSplit] = useState(String(curation.roboflow_split ?? ''));
  const [notes, setNotes] = useState(item.curation_notes ?? '');
  const [tags, setTags] = useState((curation.roboflow_tags ?? []).join(', '));

  const save = useMutation({
    mutationFn: (reviewStatus?: 'approved' | 'rejected' | 'pending') => {
      const curation_json: MlCurationJson = {
        internal_number: internalNumber || undefined,
        animal_name: animalName || undefined,
        roboflow_split: (roboflowSplit as MlCurationJson['roboflow_split']) || undefined,
        roboflow_tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
      return adminFetch(`/ml/media/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...(reviewStatus ? { review_status: reviewStatus } : {}),
          curation_notes: notes,
          curation_json,
        }),
      });
    },
    onSuccess: onSaved,
  });

  const preview = item.media_kind === 'video' ? item.thumbnail_url ?? item.url : item.url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="grid max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-xl border border-neutral-700 bg-neutral-950 lg:grid-cols-2">
        <div className="bg-black">
          {preview ? (
            <img src={preview} alt="" className="h-full max-h-[50vh] w-full object-contain lg:max-h-[90vh]" />
          ) : (
            <div className="flex h-64 items-center justify-center text-neutral-500">sem preview</div>
          )}
          <a href={item.url} target="_blank" rel="noreferrer" className="block p-2 text-center text-xs text-neutral-400 hover:text-white">
            Abrir mídia original
          </a>
        </div>
        <div className="flex flex-col overflow-y-auto p-4">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h2 className="font-semibold">Revisar item #{item.id}</h2>
              <p className="text-xs text-neutral-400">
                {item.media_kind} · cliente #{item.client_id}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-3 text-sm">
            <Field label="Nº animal (export)" value={internalNumber} onChange={setInternalNumber} />
            <Field label="Nome" value={animalName} onChange={setAnimalName} />
            <label className="block">
              <span className="mb-1 block text-neutral-400">Split Roboflow</span>
              <select
                value={roboflowSplit}
                onChange={(e) => setRoboflowSplit(e.target.value)}
                className="w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2"
              >
                <option value="">Automático</option>
                <option value="train">train</option>
                <option value="valid">valid</option>
                <option value="test">test</option>
              </select>
            </label>
            <Field label="Tags (vírgula)" value={tags} onChange={setTags} />
            <label className="block">
              <span className="mb-1 block text-neutral-400">Notas de curadoria</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2"
              />
            </label>
          </div>

          <div className="mt-auto flex flex-wrap gap-2 pt-4">
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate('approved')}
              className="flex-1 rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white"
            >
              Aprovar
            </button>
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate('rejected')}
              className="rounded-lg bg-red-900 px-4 py-2 text-sm"
            >
              Rejeitar
            </button>
            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate(undefined)}
              className="rounded-lg border border-neutral-700 px-4 py-2 text-sm"
            >
              Salvar metadados
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-neutral-400">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2"
      />
    </label>
  );
}
